import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router';
import { routes } from '@/constant/routes.const';
import styled from 'styled-components';
import { registerMedicalRefundInfo, verifyKoreanAccount } from '@/api';
import ReceiptLayout from '@/components/MedicalReceipt/ReceiptLayout';
import CheckboxWithLabel from '@/components/common/CheckboxWithLabel';
import ProgressBar from '@/components/common/ProgressBar';
import EmailVerification from '@/components/MedicalCustomerRefund/EmailVerification';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import {
  IAddressTranslation,
  IRefundInfoForm,
  IRegisterRefundInfoResponse,
  VerifyAccountPayload,
} from '@/types/common.type';
import { IGlobalState } from '@/store';
import LoadingView from '@/components/common/LoadingView';
import useCheckEmailAuth from '@/hooks/useCheckEmailAuth';
import CashPickupGuideCheckbox from '@/components/MedicalCustomerRefund/CashPickupGuideCheckbox';
import { isNationIncluded, tanslateChFromToEnAddress } from '@repo/utils';
import useGetAccountInfo from '@/hooks/useGetAccountInfo';
import {
  InfoForm,
  InfoFormBasic,
  PreviousRefundModal,
  Toast,
} from '@repo/components';
import { usePopToast } from '@repo/hooks';
import { ICON_CHECK_CIRCLE_FILL_GREEN } from '@repo/assets';
import { CASH_PICKUP_NATIONS, MAIN_NATIONS } from '@repo/constants';
import { Button } from '@successmode-ktp/kui';

function MedicalCustomerRefund() {
  const {
    passportNumber,
    nationality,
    verifiedEmail,
    email,
    paymentStatus,
    isSwiftCodeRequired,
    isCashPickup,
    isUploadRequired,
  } = useAppSelector((state) => state) as IGlobalState;

  const [isFirstTermSelected, setIsFirstTermSelected] = useState(true);
  const [isSecondTermSelected, setIsSecondTermSelected] = useState(true);
  const [isBtnActive, setIsBtnActive] = useState(false);
  const [isVerified, setIsVerified] = useState(verifiedEmail === email);
  const { mutateAsync: checkEmailAuthMutate } = useCheckEmailAuth(
    email,
    nationality,
  );
  const isFrequentCountry = isNationIncluded(MAIN_NATIONS, nationality);
  const [currentStep, setCurrentStep] = useState(1); //현재 단계
  const targetStep = isFrequentCountry ? 2 : 1; //마지막 단계
  const history = useHistory();
  const {
    handleSubmit,
    register,
    getValues,
    watch,
    formState: { errors },
    setValue,
    reset,
  } = useForm<IRefundInfoForm>();
  const { firstName, bank, lastName, phoneNumber, bankAccount } = watch();
  const dispatch = useAppDispatch();
  const {
    selectedGender,
    setSelectedGender,
    selectedAccountInfo,
    setSelectedAccountInfo,
    selectedAccountType,
    setSelectedAccountType,
    selectedBank,
    setSelectedBank,
    selectedRegion,
    setSelectedRegion,
    selectedTown,
    setSelectedTown,
    isBankCardInquiry,
    setIsBankCardInquiry,
    isHkgNation,
    setIsHkgNation,
    isOpenLoad,
    setIsOpenLoad,
    beforeAccountInfo,
    isFetching,
    setAccountInfo,
  } = useGetAccountInfo(nationality, passportNumber!, setValue);
  const [isCallAccountInfo, setIsCallAccountInfo] = useState(false);
  const { isPop, popToast } = usePopToast();

  useEffect(() => {
    if (
      nationality === 'CHN' ||
      nationality === 'HKG' ||
      nationality === 'MAC'
    ) {
      dispatch({
        type: 'UPDATE_USER_INFO',
        payload: {
          nationality: isHkgNation ? 'HKG' : 'CHN',
        },
      });
    }
    reset();
  }, [isHkgNation]);

  //현재 단계, 환급 빈도수 상위 18개국 여부에 따라 버튼 활성화
  useEffect(() => {
    const isActive = Boolean(
      currentStep === targetStep
        ? firstName &&
            lastName &&
            phoneNumber &&
            isFirstTermSelected &&
            isSecondTermSelected
        : true,
    );
    setIsBtnActive(isActive);
  }, [
    firstName,
    lastName,
    phoneNumber,
    isFirstTermSelected,
    isSecondTermSelected,
  ]);

  //@ts-ignore
  const { mutate } = useMutation<IRegisterRefundInfoResponse, AxiosError, any>(
    (payload) => registerMedicalRefundInfo(payload),
    {
      retry: false,
      onSuccess: ({ isForTemporaryStorage }) => {
        if (isForTemporaryStorage) return;
        if (isFrequentCountry || nationality === 'KOR') {
          if (currentStep !== targetStep) {
            setCurrentStep(currentStep + 1);
            window.scrollTo({ behavior: 'smooth', top: 0 });
          } else {
            history.push(routes.medicalRefundResult);
          }
        } else {
          dispatch({
            type: 'UPDATE_HOME_MODAL_STATE',
            payload: {
              type: 'CUSTOM',
              isActive: true,
              data: {
                title: 'Information has been Entered',
                content: `We will send an email within 10 days\nto request the information needed for\nprocessing refunds.\nPlease check your mailbox`,
                btnText: 'OK',
                btnCallback: () => {
                  history.push(routes.medicalRefundResult);
                },
              },
            },
          });
        }
      },
    },
  );

  const onSubmit = async () => {
    const isChineseNationality = nationality === 'CHN';
    const isStepTwo =
      (isFrequentCountry && currentStep === 2) || !isFrequentCountry;
    let enAddress: IAddressTranslation = {
      stateProvinceRegionEng: '',
      cityEng: '',
      addressEng: '',
      addressDetailEng: '',
    };

    if (isChineseNationality) {
      const { stateProvinceRegion, city, address, addressDetail } = getValues();
      enAddress = (await tanslateChFromToEnAddress(
        stateProvinceRegion,
        city,
        address,
        addressDetail,
      )) as IAddressTranslation;
    }

    const registerAccountInfo = () => {
      mutate({
        passportNumber,
        register: currentStep === targetStep,
        hasForceRefunded: isUploadRequired,
        accountInfo: {
          ...getValues(),
          gender: selectedGender,
          accountType: selectedAccountType,
          accountInfo: selectedAccountInfo,
          isBankCardInquiry,
          isHkgNation,
          stateProvinceRegionEng: enAddress.stateProvinceRegionEng,
          cityEng: enAddress.cityEng,
          addressEng: enAddress.addressEng,
          addressDetailEng: enAddress.addressDetailEng,
        },
      });
    };

    if (isStepTwo && isChineseNationality) {
      return dispatch({
        type: 'UPDATE_HOME_MODAL_STATE',
        payload: {
          isActive: true,
          type: 'CUSTOM',
          data: {
            title: '您输入的名字对吗?',
            content: `${getValues('firstName')}${getValues(
              'lastName',
            )}\n${getValues('firstNameEng')}${getValues('lastNameEng')}`,
            subBtnText: '重新输入',
            btnText: '确认',
            btnCallback: () => registerAccountInfo(),
          },
        },
      });
    }
    registerAccountInfo();
  };

  const submitWithMailCheck = async () => {
    if (isFrequentCountry) {
      return handleSubmit(onSubmit)();
    }
    const res = await checkEmailAuthMutate({ email, passportNumber });
    if (res) {
      setIsVerified(true);
      setIsBtnActive(true);
      handleSubmit(onSubmit)();
    }
  };

  const hasChnBank = () => {
    if (nationality === 'CHN') {
      return isBankCardInquiry && getValues('bankAccount') && getValues('bank');
    }
    return true;
  };

  const { mutate: verifyKorAccount, isLoading } = useMutation(
    (payload: VerifyAccountPayload) => verifyKoreanAccount(payload),
  );

  console.log(currentStep);

  return (
    <ContainerWrapper className='notranslate'>
      <ReceiptLayout hasPadding={false}>
        <ProgressBar
          percent={targetStep === 2 ? (currentStep === 1 ? 33 : 66) : 50}
        />
        <Inner>
          {!isCashPickup && (
            <Title>
              {(() => {
                if (nationality === 'KOR') {
                  return `${passportNumber}님의\n정보를 작성해주세요`;
                }
                return `${passportNumber}!\nPlease enter your information.`;
              })()}
            </Title>
          )}
          {isCashPickup && currentStep === 2 && (
            <Title>Enter Information for Cash Pickup.</Title>
          )}
          {isCallAccountInfo && (
            <WarningTextWrapper>
              <WarningText>
                <img src={ICON_CHECK_CIRCLE_FILL_GREEN} alt='' width={24} />
                {nationality === 'KOR'
                  ? '기존 환급 정보를 불러왔습니다.'
                  : 'The previous information has been loaded.'}
                <br />
                {nationality === 'KOR'
                  ? '입력된 정보를 다시 한번 확인해주세요.'
                  : 'Please double-check the information.'}
              </WarningText>
            </WarningTextWrapper>
          )}
          {currentStep === 1 && !isFrequentCountry && (
            <EmailVerification
              getValues={getValues}
              isBankCardInquiry={isBankCardInquiry}
              setIsBtnActive={setIsBtnActive}
              isHkgNation={isHkgNation}
              isVerified={isVerified}
              setIsVerified={setIsVerified}
            />
          )}
          <Wrapper>
            {isFrequentCountry &&
              currentStep === 1 &&
              (isFetching ? (
                <LoadingView />
              ) : (
                <InfoForm
                  errors={errors}
                  register={register}
                  getValues={getValues}
                  selectedAccountInfo={selectedAccountInfo}
                  setSelectedAccountInfo={setSelectedAccountInfo}
                  selectedBank={selectedBank}
                  setSelectedBank={setSelectedBank}
                  setValue={setValue}
                  selectedAccountType={selectedAccountType}
                  setSelectedAccountType={setSelectedAccountType}
                  selectedGender={selectedGender}
                  setSelectedGender={setSelectedGender}
                  isBankCardInquiry={isBankCardInquiry}
                  setIsBankCardInquiry={setIsBankCardInquiry}
                  selectedRegion={selectedRegion}
                  setSelectedRegion={setSelectedRegion}
                  selectedTown={selectedTown}
                  setSelectedTown={setSelectedTown}
                  isHkgNation={isHkgNation}
                  setIsHkgNation={setIsHkgNation}
                  nationality={nationality}
                  paymentStatus={paymentStatus}
                  isSwiftCodeRequired={isSwiftCodeRequired}
                />
              ))}
            {((isFrequentCountry && currentStep === 2) ||
              !isFrequentCountry) && (
              <InfoFormBasic
                isMedical
                register={register}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
                reset={reset}
                bankName={bank}
                bankAccount={bankAccount}
                selectedAccountType={selectedAccountType}
                setSelectedAccountType={setSelectedAccountType}
                nationality={nationality}
                isSwiftCodeRequired={isSwiftCodeRequired}
                onVerifyKorAccount={verifyKorAccount}
                isLoading={isLoading}
              />
            )}
            {currentStep === targetStep && (
              <>
                <CheckboxWithLabel
                  isTopLine
                  isActive={isFirstTermSelected}
                  setIsActive={setIsFirstTermSelected}
                  label={
                    nationality === 'KOR'
                      ? '개인정보 수집 및 이용안내에 동의합니다.'
                      : 'I will consent to '
                  }
                  highlightLabel={
                    nationality === 'KOR'
                      ? ''
                      : 'disclosure of personal information'
                  }
                />
                <CheckboxWithLabel
                  isActive={isSecondTermSelected}
                  setIsActive={setIsSecondTermSelected}
                  label={
                    nationality === 'KOR'
                      ? '고유식별정보 수집에 동의합니다.'
                      : 'I agree to '
                  }
                  highlightLabel={
                    nationality === 'KOR'
                      ? ''
                      : 'collection and use of personal information'
                  }
                />
              </>
            )}
            <Button
              type='button'
              size='lg'
              disabled={!isBtnActive || !hasChnBank()}
              onClick={
                isVerified ? handleSubmit(onSubmit) : submitWithMailCheck
              }
              style={{ margin: '20px 0 40px' }}
            >
              {(() => {
                if (currentStep === targetStep) {
                  if (nationality === 'KOR') {
                    return '완료';
                  } else {
                    return 'CHECK';
                  }
                } else {
                  if (isCashPickup) return 'Enter Information';
                  return 'NEXT';
                }
              })()}
            </Button>
          </Wrapper>
        </Inner>
      </ReceiptLayout>
      <PreviousRefundModal
        isOpen={isOpenLoad}
        nationality={nationality}
        onClose={() => {
          setIsOpenLoad(false);
          setIsCallAccountInfo(false);
        }}
        onClick={() => {
          setAccountInfo(beforeAccountInfo, nationality);
          setIsOpenLoad(false);
          setIsCallAccountInfo(true);
          popToast(7000);
        }}
      />
      <Toast
        isDisplay={isPop}
        text={
          nationality === 'KOR'
            ? '기존 환급 정보를 불러왔습니다. 입력된 정보를 다시 한번 확인해주세요.'
            : 'The information has been loaded. Please double-check the information.'
        }
      />
    </ContainerWrapper>
  );
}

export default MedicalCustomerRefund;

const ContainerWrapper = styled.div``;

const Inner = styled.div`
  padding: 24px 0 40px 0px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 21px;
  color: #1856cd;
  white-space: pre-wrap;
  line-height: 150%;
  text-align: left;
  font-weight: 500;
  width: 100%;
  display: flex;
  padding: 0px 20px 16px 20px;
  gap: 6px;
`;

const WarningTextWrapper = styled.div`
  padding: 0 20px;
  width: 100%;
`;

const WarningText = styled.p`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 16px 24px;
  margin-bottom: 20px;
  border-radius: 8px;
  background: var(--KTP-Grayscale-Gray_12, #f4f4f4);
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: -0.28px;
`;

const Wrapper = styled.div`
  padding: 0 20px;
  width: 100%;
`;
